import React, { useState, useCallback, useEffect, useContext, useRef } from 'react'
import { useStyles } from './styles'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { SkillBiteEditSlice } from '../../../../../reducers'
import { Box, Typography, Switch, Divider, Fab, CircularProgress } from '@mui/material'
import { ActionButton, Input } from '@emerald-works-nova/components'
import { skillBiteEmailFormSchema } from '../../../../../util/skill-bite-schemas'
import { yupResolver } from '@hookform/resolvers/yup'
import SaveRoundedIcon from '@mui/icons-material/SaveRounded'
import EmailPreviewModal from '../email-preview-modal'
import { useBeforeUnload } from '../../../../../hooks'
import { unstable_usePrompt as usePrompt } from 'react-router-dom'
import { EmailNavigationContext } from '../..'
import { useSelector } from 'react-redux'

const getDefaultEmailConfig = (session) => {
  const isFirstSession = session?.order === 1
  if (isFirstSession) {
    return {
      headline: '',
      intro: 'Dear $$USER_NAME$$,\n\nWelcome to your first Skill Bites session!\n\n',
      cta: ''
    }
  }
  return {
    headline: '',
    commitmentCopy: 'In your previous session, you made this commitment:\n\n$$USER_COMMITMENT$$\n\n',
    intro: 'Dear $$USER_NAME$$,\n\nWelcome back to your Skill Bites journey!\n\n$$COMMITMENT_COPY$$\n\n',
    cta: ''
  }
}

const EmailConfigForm = ({ session }) => {
  const classes = useStyles()
  const { skillBiteId } = useParams()
  const {
    headline,
    commitmentCopy,
    intro,
    cta
  } = session?.emailConfig || { ...getDefaultEmailConfig(session) }

  const canEdit = useSelector(SkillBiteEditSlice.selectors.selectEditMode)
  const [hasCommitmentCopy, setHasCommitmentCopy] = useState(commitmentCopy !== undefined)
  const isPublished = useSelector(SkillBiteEditSlice.selectors.selectIsPublished)
  const [open, setOpen] = useState(false)
  const [updateSessionEmailConfig] = useEvent([SkillBiteEditSlice.eventBus.updateSessionEmail])
  const commitmentCopyRef = useRef()
  const { emailFormRef } = useContext(EmailNavigationContext)
  const isReadOnly = !canEdit || updateSessionEmailConfig.isWorking
  const form = useForm({
    defaultValues: {
      ...getDefaultEmailConfig(session)
    },
    resolver: yupResolver(skillBiteEmailFormSchema),
    mode: 'all'
  })

  const pageContainerRef = useRef()
  const resetPageContainerScroll = useCallback(() => {
    pageContainerRef.current.scrollTop = 0
  }, [])

  const { control, formState } = form
  const { isValid, isDirty } = formState
  const resetForm = useCallback(() => {
    form.reset({
      headline,
      commitmentCopy,
      intro,
      cta
    })
    commitmentCopyRef.current = commitmentCopy
    setHasCommitmentCopy(commitmentCopy !== undefined)
  }, [form, headline, commitmentCopy, intro, cta])

  // warns the user if they try to leave without saving the changes they made
  useBeforeUnload(() => isDirty && canEdit)
  usePrompt({
    message: 'You have unsaved changes on your session email config form, are you sure you want to exit without saving?',
    when: isDirty && canEdit
  })

  useEffect(() => {
    resetPageContainerScroll()
    resetForm()
  }, [session.id, resetForm, resetPageContainerScroll])

  useEffect(() => {
    emailFormRef.current = form
    return () => {
      emailFormRef.current = null
    }
  }, [form, emailFormRef])

  const handleSwitch = () => setHasCommitmentCopy(checked => {
    if (checked) setHasCommitmentCopy(false)
    const newValue = !checked

    if (newValue === false) {
      // Cache commitment copy previous value
      commitmentCopyRef.current = form.getValues('commitmentCopy')

      form.setValue('commitmentCopy', '', { shouldDirty: true })
    } else {
      if (commitmentCopyRef.current) {
        // Restore cached value
        form.setValue('commitmentCopy', commitmentCopyRef.current)
      }
    }
    return newValue
  })

  const handlePreview = () => setOpen(true)

  const onSubmit = useCallback((data) => {
    updateSessionEmailConfig.trigger({
      skillBiteId,
      sessionId: session.id,
      emailConfig: {
        ...data,
        commitmentCopy: hasCommitmentCopy ? data.commitmentCopy : undefined
      }
    })
  }, [hasCommitmentCopy, session.id, skillBiteId, updateSessionEmailConfig])

  if (!session) return null

  return (
    <Box className={classes.root}>
      <Box
        display='flex'
        flexDirection='row'
        alignItems='center'
        justifyContent='space-between'
        sx={{
          borderBottom: '1px solid #E0E0E0',
          width: '100%'
        }}
      >
        <Typography variant='body2' data-test='email-session-name'>
          Email Session: <strong>{session.name}</strong>
        </Typography>
        <ActionButton
          dataTest='email-preview-button'
          sx={{ minWidth: 120, marginBottom: '10px' }}
          onClick={handlePreview}
        >
          Email Preview
        </ActionButton>
      </Box>
      <Box
        display='flex'
        flexDirection='column'
        sx={{
          padding: '16px'
        }}
        ref={pageContainerRef}
        className={classes.pageContainer}
      >
        <form onSubmit={form.handleSubmit(onSubmit)} className={classes.form}>
          <Box>
            <Typography sx={{ fontWeight: 500 }}>
              Headline
            </Typography>
            <Typography>
              The headline that will appear on Email.
            </Typography>
            <Input
              className={classes.input}
              control={control}
              disabled={isReadOnly}
              rules={{ required: true }}
              label='Headline'
              name='headline'
            />
          </Box>
          <Divider />
          {session.order > 1 && (
            <>
              <Box>
                <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontWeight: 500 }}>
                    Commitment Copy
                  </Typography>
                  {canEdit && (
                    <Switch
                      color='primary'
                      disabled={isReadOnly}
                      checked={hasCommitmentCopy}
                      onChange={handleSwitch}
                    />
                  )}
                </Box>
                <Typography>
                  Use this block to pull in the user’s commitment from the previous session, using the variable <strong>$$USER_COMMITMENT$$</strong>
                </Typography>
                {hasCommitmentCopy && (
                  <Input
                    className={classes.input}
                    control={control}
                    rules={{ required: false }}
                    disabled={isReadOnly}
                    label='Commitment Copy'
                    name='commitmentCopy'
                    multiline
                    rows={5}
                    rowsMax={15}
                  />
                )}
              </Box>
              <Divider />
            </>
          )}
          <Box>
            <Typography sx={{ fontWeight: 500 }}>
              Email Body Text
            </Typography>
            <Typography>
              This block is for the main copy of the email to the user.
              To pull in the user's name, use the variable <strong>$$USER_NAME$$</strong>
              {hasCommitmentCopy && (
                <Typography>
                  To pull in the user's commitment from the Commitment Copy above, simply use the variable <strong>$$COMMITMENT_COPY$$</strong> on it's own.
                  Note: the Commitment Copy block will only appear if the user typed a commitment.
                </Typography>
              )}
            </Typography>
            <Input
              className={classes.input}
              control={control}
              disabled={isReadOnly}
              rules={{ required: true }}
              label='Email Copy'
              name='intro'
              multiline
              rows={5}
              rowsMax={15}
            />
          </Box>
          <Divider />
          <Box>
            <Typography sx={{ fontWeight: 500 }}>
              CTA Label
            </Typography>
            <Typography>
              This will appear as Action Button's label in the email.
            </Typography>
            <Input
              className={classes.input}
              control={control}
              disabled={isReadOnly}
              rules={{ required: true }}
              label='CTA Label'
              name='cta'
            />
          </Box>
          {canEdit && (
            <Box display='flex' flexDirection='row' justifyContent='flex-end' sx={{ position: 'sticky', bottom: 16, right: 16, mt: 2, mb: 2 }}>
              <Fab
                variant='extended' color='primary' size='medium' type='submit'
                data-test='skill-bite-email-save-button'
                disabled={updateSessionEmailConfig.isWorking || !isValid || !isDirty}
              >
                {updateSessionEmailConfig.isWorking ? <CircularProgress size='1em' sx={{ mr: 1 }} /> : <SaveRoundedIcon sx={{ mr: 1 }} />}
                {isPublished ? 'Save & Publish' : 'Save'}
              </Fab>
            </Box>
          )}
        </form>
      </Box>
      <EmailPreviewModal
        session={session}
        open={open}
        setOpen={setOpen}
      />

    </Box>
  )
}

export default EmailConfigForm
